import type { ApiErrorEnvelope } from "@m8t-stack/api-contract";

interface HintRule {
  code: string;
  reason?: string;
  hint: (e: ApiErrorEnvelope) => string;
}

function getDetailField(envelope: ApiErrorEnvelope, key: string): string | undefined {
  if (typeof envelope.details === "object" && envelope.details !== null) {
    const val = (envelope.details as Record<string, unknown>)[key];
    return typeof val === "string" ? val : undefined;
  }
  return undefined;
}

// BAD_REQUEST validation reasons (missing_handle, invalid_handle, empty_handle,
// handle_too_long, missing_display_name, display_name_empty, missing_identities,
// invalid_identities, invalid_channel_id, channel_id_empty, channel_id_too_long,
// etc.) intentionally have no entries here — the backend's `message` field is
// already self-explanatory (e.g. "handle must not be empty.") and adds nothing
// when surfaced through a CLI hint. Only add a rule when the hint provides
// genuinely new info (e.g. a remediation command).
const BACKEND_RULES: HintRule[] = [
  // Auth-related
  {
    code: "UNAUTHENTICATED",
    reason: "missing_bearer",
    hint: () => "you are not signed in to Azure — run 'az login' first.",
  },
  {
    code: "UNAUTHENTICATED",
    reason: "header_missing",
    hint: () =>
      "the gateway didn't receive your identity headers — this is likely a gateway misconfiguration.",
  },
  {
    code: "UNAUTHENTICATED",
    hint: () => "your Azure login may have expired — run 'az login' to re-authenticate.",
  },

  // CONFLICT — handle_exists (POST collision)
  {
    code: "CONFLICT",
    reason: "handle_exists",
    hint: (e) => {
      const handle = getDetailField(e, "handle") ?? "<handle>";
      return `use 'm8t team add-identity ${handle} --<channel> <id>' to add another channel identity to the existing member.`;
    },
  },

  // CONFLICT — identity_claimed (PATCH cross-handle collision)
  {
    code: "CONFLICT",
    reason: "identity_claimed",
    hint: (e) => {
      const otherHandle = getDetailField(e, "handle") ?? "<handle>";
      const channel = getDetailField(e, "channel") ?? "<channel>";
      return `the ${channel} identity already belongs to '${otherHandle}'. Remove it from '${otherHandle}' first, or use a different channel ID.`;
    },
  },

  // NOT_FOUND
  {
    code: "NOT_FOUND",
    reason: "team_member_not_found",
    hint: () => "try 'm8t team list' to see all team members.",
  },

  // BAD_REQUEST
  {
    code: "BAD_REQUEST",
    reason: "no_identities_provided",
    hint: () => "specify at least one of --telegram, --slack, --teams.",
  },
  {
    code: "BAD_REQUEST",
    reason: "invalid_handle",
    hint: () =>
      "handle must be lowercase letters, digits, and hyphens; must start with a letter or digit.",
  },
  {
    code: "BAD_REQUEST",
    reason: "handle_too_long",
    hint: () => "handle must be 64 chars or fewer.",
  },
  {
    code: "BAD_REQUEST",
    reason: "empty_patch",
    hint: () =>
      "PATCH body must contain at least one of: --display, an identity flag, or --remove-<channel>.",
  },
  {
    code: "BAD_REQUEST",
    reason: "empty_patch_would_orphan",
    hint: (e) => {
      const handle = getDetailField(e, "handle") ?? "<handle>";
      return `removing those channels would leave '${handle}' with no identities. Use 'm8t team remove ${handle}' to remove the member entirely.`;
    },
  },
  {
    code: "BAD_REQUEST",
    reason: "unknown_channel",
    hint: () => "channel must be one of: telegram, slack, teams.",
  },

  // BAD_REQUEST — binding-validation reasons
  {
    code: "BAD_REQUEST",
    reason: "no_adapter_registered",
    hint: (e) => {
      const channel = getDetailField(e, "channel") ?? "<channel>";
      return `no adapter for channel '${channel}'. The channel-specific adapter is not registered in this build.`;
    },
  },
  {
    code: "BAD_REQUEST",
    reason: "invalid_binding_id",
    hint: () =>
      "slug must be lowercase letters, digits, and hyphens; must start with a letter or digit. Example: cmo-tg, cpo-slack.",
  },
  {
    code: "BAD_REQUEST",
    reason: "binding_id_too_long",
    hint: () => "slug must be 64 chars or fewer.",
  },
  {
    code: "BAD_REQUEST",
    reason: "invalid_status_filter",
    hint: () => "--status must be 'active' or 'orphaned'.",
  },
  {
    code: "BAD_REQUEST",
    reason: "invalid_channel_filter",
    hint: () => "--channel must be one of: telegram, slack, teams.",
  },
  {
    code: "BAD_REQUEST",
    reason: "cleanup_active_binding",
    hint: (e) => {
      const bindingId = getDetailField(e, "bindingId") ?? "<id>";
      return `binding '${bindingId}' is active. Use 'm8t bind remove ${bindingId}' instead — cleanup is for orphaned bindings only.`;
    },
  },

  // CONFLICT — binding_exists
  {
    code: "CONFLICT",
    reason: "binding_exists",
    hint: (e) => {
      const channel = getDetailField(e, "channel") ?? "<channel>";
      const bindingId = getDetailField(e, "bindingId") ?? "<id>";
      return `binding '${bindingId}' already exists on channel '${channel}'. Use 'm8t bind show ${bindingId}' to inspect or 'm8t bind remove ${bindingId}' first.`;
    },
  },

  // NOT_FOUND — binding_not_found
  {
    code: "NOT_FOUND",
    reason: "binding_not_found",
    hint: (e) => {
      const bindingId = getDetailField(e, "bindingId") ?? "<id>";
      return `no binding '${bindingId}' found. Run 'm8t bind list' to see all bindings.`;
    },
  },

  // BAD_REQUEST — webhook-registration rejection
  {
    code: "BAD_REQUEST",
    reason: "webhook_registration_rejected",
    hint: () =>
      "Telegram rejected the bot token. Double-check the token from BotFather and that it isn't revoked.",
  },

  // UPSTREAM_FAILED — webhook-registration transient failure
  {
    code: "UPSTREAM_FAILED",
    reason: "webhook_registration_failed",
    hint: () =>
      "Couldn't reach Telegram to register the webhook. Try the command again in a moment.",
  },

  // UPSTREAM_FAILED — KV + cascade reasons
  {
    code: "UPSTREAM_FAILED",
    reason: "key_vault_unavailable",
    hint: () =>
      "Key Vault was unreachable. Check that the gateway's managed identity has the 'Key Vault Secrets Officer' role on the configured vault.",
  },
  {
    code: "UPSTREAM_FAILED",
    reason: "kv_secret_malformed",
    hint: () =>
      "the Key Vault secret for this binding is in an unexpected shape. Manual cleanup may be required — contact your m8t admin.",
  },
  // NOTE: cascade_partial_failure is handled INLINE in the bind remove +
  // bind cleanup command files (the rendering needs the structured
  // details.partial.{completedSteps, failedStep, originalMessage}), so
  // no entry here intentionally.

  // UPSTREAM_FAILED
  {
    code: "UPSTREAM_FAILED",
    reason: "table_storage_partial_write",
    hint: () =>
      "partial write — run 'm8t team show <handle>' to see what was created, then 'm8t team add-identity' to fill in the rest.",
  },
  {
    code: "UPSTREAM_FAILED",
    reason: "table_storage_unavailable",
    hint: () => "Azure Table Storage is temporarily unavailable — try again in a moment.",
  },

  // RATE_LIMITED
  {
    code: "RATE_LIMITED",
    hint: () => "wait a moment, then try again.",
  },

  // INTERNAL
  {
    code: "INTERNAL",
    reason: "config",
    hint: () =>
      "the gateway has a configuration error — check the Container App's environment variables.",
  },
];

const LOCAL_RULES: Record<string, string> = {
  AGENT_CREATE_VERSION_FAILED: "Re-run with `--verbose` for the full response body.",
  AGENT_CREATE_VERSION_NO_VERSION:
    "Foundry returned 2xx but no version — likely an API regression. Re-try; if it persists, file an issue.",
  AGENT_ENDPOINT_AUTH_PATCH_FAILED: "Re-run with `--verbose` for the full response body.",
  AGENT_GET_FAILED: "Re-run with `--verbose` for the full response body.",
  AGENT_NOT_FOUND:
    "Use `m8t brain create <name>` to create the worker first, OR run `m8t deploy <name>` to register an existing agent.",
  AGENT_REDEPLOY_FAILED: "Re-run with `--verbose` for the full response body.",
  AGENT_REDEPLOY_NO_VERSION:
    "Foundry returned 2xx but no version. Re-try; if it persists, file an issue.",
  AGENT_YAML_NOT_FOUND:
    "Deploy the worker via `m8t deploy <name>` or the architect first.",
  APP_HEALTH_FAILED:
    "Run `m8t brain check-app` for details, then re-run this command.",
  APP_UNINSTALL_FAILED:
    "GitHub App-uninstall DELETE failed. Re-run with `--keep-app-install` if you want to skip this step.",
  ARM_AUTH: "Run `az login` and retry.",
  // BOOTSTRAP_OCCUPANCY_UNVERIFIED and BOOTSTRAP_REINSTALL_TARGET_MISMATCH are
  // deliberately absent here: both are always thrown with an inline `hint`
  // (see bootstrap-occupancy.ts / commands/bootstrap/launch.ts), and
  // render-error.ts prefers that inline hint over this table — so a table
  // entry for either code would be dead text that could drift from what
  // actually prints. BOOTSTRAP_TARGET_OCCUPIED has no inline hint, so its
  // entry below is the live one.
  BOOTSTRAP_TARGET_OCCUPIED:
    "Pick an empty resource group with `--resource-group <new-rg>`, or run with both `--resource-group <rg>` and `--reinstall-into <rg>` (same group name) to install into the existing one anyway.",
  // m8t brain create: stageAsGitRepo step
  BRAIN_GIT_INIT_FAILED:
    "If git is not installed, install it. Otherwise re-run with --verbose.",
  BRAIN_NOT_LINKED:
    "The worker has no brain link. Use `m8t brain link` or `m8t brain create` first.",
  BRAIN_YAML_MIRROR_FAILED:
    "GitHub Contents API PUT failed. Verify the App has `contents: write` permission on the repo.",
  CONN_CREATE_FAILED:
    "Foundry connection PUT failed. Verify Cognitive Services Contributor role on the project.",
  CONN_DELETE_FAILED:
    "Foundry connection DELETE failed. Verify your ARM subscription has Contributor on the project.",
  CONN_GET_FAILED:
    "Foundry connection GET failed. Verify your ARM subscription has access to the project.",
  // rename pending: should be FOUNDRY_AUTH_FAILED
  FOUNDRY_AUTH: "Run `az login` and retry.",
  FOUNDRY_AUTH_FAILED: "Run `az login` and retry.",
  KV_URI_NOT_FOUND:
    "Pass `--kv-uri` or set `AZURE_KEYVAULT_URI` / `KEYVAULT_URI`.",
  LINK_NO_INSTALLATION_ID:
    "Internal error — the CLI command must poll for the installation id before calling linkBrain. Report at https://github.com/m8t/m8t/issues.",
  M8T_REPO_ROOT_MISSING:
    "Re-run `m8t install` from a fresh m8t checkout — the post-install hook writes ~/.m8t/repo-root.",
  PERSONA_PATH_NOT_ABSOLUTE:
    "The persona path in ~/.m8t/foundry/<worker>.yaml must be absolute. Re-deploy the worker via the architect.",
  PERSONA_PLACEHOLDER_UNSATISFIED:
    "Re-deploy the worker via the architect to repopulate fillableFieldValues, OR edit ~/.m8t/foundry/<worker>.yaml manually.",
  PERSONA_READ_FAILED:
    "Verify the persona path in ~/.m8t/foundry/<worker>.yaml; the architect should have populated it at deploy time.",

  AUTH_NOT_SIGNED_IN: "you are not signed in to Azure — run 'az login' first.",
  AUTH_EXPIRED: "your Azure login expired — run 'az login' to re-authenticate.",
  AUTH_WRONG_TENANT:
    "your active Azure session may be in a different tenant — run 'az login --tenant <id>'.",
  AUTH_NULL_TOKEN: "no token was returned from Azure — run 'az login' and try again.",
  AUTH_FAILED: "Azure token acquisition failed — re-run after 'az login'.",
  AZ_NOT_INSTALLED:
    "install the Azure CLI: https://learn.microsoft.com/cli/azure/install-azure-cli",
  AZ_COMMAND_FAILED: "an 'az' command failed — re-run with the same args to see Azure's error.",
  AZ_OUTPUT_INVALID: "the Azure CLI returned unexpected output — try 'az account show' to verify.",
  GATEWAY_DISCOVERY_ZERO:
    "the deployment may be in a different subscription — try 'az account set --subscription <other>'.",
  GATEWAY_DISCOVERY_MULTIPLE:
    "pass --subscription <id> to pick one, or run interactively from a TTY for the picker.",
  GATEWAY_DISCOVERY_SELECTION_FAILED: "no deployment was selected — re-run and pick one.",
  GATEWAY_DISCOVERY_ARM_FAILED:
    "verify you have Reader access to the subscription and 'az account show' is correct.",
  GATEWAY_DISCOVERY_NO_SUBSCRIPTION:
    "set an active subscription with 'az account set --subscription <name>'.",
  GATEWAY_DISCOVERY_NO_FQDN:
    "the Container App is missing an external ingress FQDN — check the deployment.",
  GATEWAY_DISCOVERY_MISSING_ENV:
    "the Container App is missing AZURE_CLIENT_ID or AZURE_TENANT_ID env vars — check the deployment.",
  NETWORK_FAILED:
    "verify the gateway is deployed and reachable. 'm8t config reset' to re-discover.",
  BAD_RESPONSE: "this may be a gateway version mismatch — run with --verbose for details.",
  USAGE: "see 'm8t <command> --help' for details.",
};

export function resolveBackendHint(envelope: ApiErrorEnvelope): string | undefined {
  const reason = getDetailField(envelope, "reason");
  for (const rule of BACKEND_RULES) {
    if (rule.code !== envelope.code) continue;
    if (rule.reason !== undefined && rule.reason !== reason) continue;
    return rule.hint(envelope);
  }
  return undefined;
}

export function resolveLocalHint(code: string): string | undefined {
  return LOCAL_RULES[code];
}
